/**
 * @file agent.tsx
 * @desc WFW Catalyst Embed Entry Point.
 *
 * This file is compiled by scripts/build-embed.mjs into public/embed/agent.js --
 * a single IIFE bundle that customers paste on their own websites as:
 *
 *   <script src="https://workforcewave.com/embed/agent.js"
 *           data-agent-id="agent_xxx" async></script>
 *
 * On execution the bundle:
 *   1. Reads data-agent-id from its own script tag.
 *   2. Fetches /api/voice-widget/config/[agentId] from window.location.origin.
 *   3. Validates the response (enabled flag + origin allowlist).
 *   4. Detects EU visitors via timezone heuristic; shows GDPR consent banner if needed.
 *   5. Mounts the Catalyst widget into a Shadow DOM attached to a fresh host div.
 *   6. Passes language and mode config from the API response into Catalyst props.
 *
 * Shadow DOM rationale:
 *   Customer site CSS (resets, font-face, z-index stacking contexts) cannot
 *   bleed into the widget. The widget's CSS cannot bleed out into the page.
 *   The Poppins font and a Tailwind-style reset are injected into the shadow root.
 *
 * NOT imported by Next.js -- this file is purely an esbuild entry point.
 *
 * Pitfalls applied from docs/catalyst.md sec. 10:
 *   #1  Pre-warm getUserMedia then immediately release tracks (no parallel stream)
 *   #2  ConversationProvider wraps CatalystInner (done inside Catalyst.tsx itself)
 *   #3  SSR mount gate handled -- embed runs client-only, no SSR concern
 *   #4  Mute via conversation.setMuted() only (handled inside Catalyst.tsx)
 *   #5  Same-origin origin header fallback (handled server-side)
 *   #6  Agent text_only=false must be set in ElevenLabs dashboard (documented)
 *   #7  Do not pass overrides.agent.firstMessage (handled in Catalyst.tsx)
 *   #8  firstMessage must be non-empty on ElevenLabs side (documented)
 *   #9  allowedDevOrigins in next.config.mjs (already configured)
 *   #10 Use next/script for server components (N/A -- this is client embed)
 *   #11 Param name consistency in dynamic routes (N/A)
 *   #12 libsamplerate.worklet.js bundled locally (handled in Catalyst.tsx)
 */

import React, { useState, useEffect } from 'react'
import { createRoot } from 'react-dom/client'
import { createAgentLifetime } from './agent-lifetime'
import { ConversationProvider } from '@elevenlabs/react'

import type { ResolvedBrand } from '../lib/brand/resolveBrand'
import CatalystInnerEmbed from './catalyst-inner-embed'
import { setCatalystApiBase } from '../components/Catalyst'
import { ConsentBanner, isEuTimezone, readConsentFromStorage, writeConsentToStorage } from './consent-banner'
import { isValidLanguageCode } from '../lib/i18n/widget-languages'

// esbuild replaces __WFW_AGENT_CSS__ at build time with the CSS string.
declare const __WFW_AGENT_CSS__: string

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * Public display fields for a white-label partner brand.
 * Returned by the config endpoint when the agent belongs to a partner customer.
 * Only contains safe, non-sensitive display data (slug, name, colors, logo).
 */
interface PartnerBrand {
  /** Partner slug (e.g. 'loulou') -- used as a key in logs and local storage. */
  slug: string
  /** Partner company name (e.g. 'Lou Lou'). Shown in attribution text. */
  name: string
  /** Partner primary hex color (e.g. '#d97706'). Overrides the agent's widget color when set. */
  color: string
  /** URL of the partner logo. Empty string when no logo is configured. */
  logoUrl: string
  /** Attribution line rendered below the collapsed pill (e.g. 'Powered by Lou Lou'). */
  attributionText: string
}

/**
 * Shape returned by GET /api/voice-widget/config/[agentId] when enabled=true.
 * Extended with Phase 1 multi-language and mode fields,
 * and Phase 4 partner brand object.
 */
interface AgentConfig {
  enabled: boolean
  agentName?: string
  buttonText?: string
  firstMessage?: string
  color?: string
  avatarUrl?: string
  allowedOrigins?: string[]
  /** ISO 639-1 language codes supported by this agent. Default: ['en'] */
  languages?: string[]
  /** Interaction modes enabled: 'voice' and/or 'text'. Default: ['voice','text'] */
  modes?: string[]
  /** Optional custom embed domain (e.g. 'chat.acme.com'); not used client-side */
  webWidgetCustomDomain?: string | null
  /**
   * Phase 4: partner brand returned from the config endpoint.
   * Null when the agent belongs to a direct WFW customer (not a reseller partner).
   * Non-null when the customer is managed by a white-label partner (e.g. Lou Lou).
   * The bundle uses this to render partner logo, color, and attribution text instead of WFW defaults.
   */
  partnerBrand?: PartnerBrand | null
}

// ---------------------------------------------------------------------------
// Origin validation (mirrors server-side originAllowlist.ts -- defense in depth)
// ---------------------------------------------------------------------------

/**
 * Returns true if the given origin matches any entry in the allowed origins list.
 *
 * Supported formats:
 *   'https://acme.com'        -- exact match
 *   '*.acme.com'              -- one subdomain level under acme.com (https only)
 *   'http://localhost:3000'   -- exact match (dev)
 *   'https://wfw.local'       -- exact match (dev)
 *
 * Case-insensitive; trailing slashes stripped. Mirrors isOriginAllowed() on the server.
 */
function isOriginAllowed(origin: string, allowedOrigins: string[]): boolean {
  if (!allowedOrigins || allowedOrigins.length === 0) return true

  const normalise = (s: string) => s.toLowerCase().replace(/\/+$/, '')
  const o = normalise(origin)

  for (const entry of allowedOrigins) {
    const e = normalise(entry)

    if (e.startsWith('*.')) {
      // Wildcard: *.acme.com matches https://sub.acme.com (one level only)
      const baseDomain = e.slice(2)
      const withoutScheme = o.replace(/^https?:\/\//, '')
      const parts = withoutScheme.split('.')
      const domainParts = baseDomain.split('.')

      if (parts.length === domainParts.length + 1) {
        const suffix = parts.slice(1).join('.')
        if (suffix === baseDomain && o.startsWith('https://')) {
          return true
        }
      }
      // Also match the bare domain itself for *.acme.com
      if (o === `https://${baseDomain}`) {
        return true
      }
    } else if (o === e) {
      return true
    }
  }

  return false
}

// ---------------------------------------------------------------------------
// Minimal ResolvedBrand factory
// ---------------------------------------------------------------------------

/**
 * Builds a minimal ResolvedBrand-shaped object from the config API response.
 * The embed environment has no Next.js server context, so we synthesise a brand
 * from the widget config. Non-critical fields (SEO, footer, features, etc.) get
 * safe defaults. The widget only reads: colors.accent, colors.accentDark,
 * name, logo.icon, catalyst.agentId, catalyst.agentName, cta.href, slug.
 *
 * Phase 4 partner branding:
 *   When config.partnerBrand is non-null, the brand reflects the partner (e.g. Lou Lou)
 *   rather than Workforce Wave. The partner's color is used as the accent, their logo
 *   as the icon, and their name in the attribution line under the pill.
 *   If the agent admin also set a per-agent color (config.color), that takes precedence
 *   over the partner's color so admins can still customize per-customer.
 */
function buildEmbedBrand(agentId: string, config: AgentConfig): ResolvedBrand {
  const pb = config.partnerBrand ?? null

  // Color priority: per-agent config.color > partner brand color > WFW default
  const accent = config.color ?? (pb ? pb.color : '#4f46e5')

  // accentDark: slightly darker version. We re-use the accent for simplicity --
  // Catalyst uses accentDark for hover states only, and the difference is subtle.
  const accentDark = accent

  // Partner attribution line or WFW default -- rendered below the collapsed pill.
  // The Catalyst widget reads brand.name for this label.
  // When partnerBrand is present, use partner name; otherwise fall back to 'Workforce Wave'.
  const brandName = pb ? pb.name : 'Workforce Wave'
  const brandSlug = pb ? pb.slug : 'embed'
  const brandDomain = pb ? '' : 'workforcewave.com'

  // Logo: per-agent avatar takes priority; then partner logo; then empty (mic icon fallback).
  const logoIcon = config.avatarUrl ?? (pb ? pb.logoUrl : '')

  return {
    slug: brandSlug,
    // Mark as partner so downstream code (e.g. attribution text) can branch if needed.
    isPartner: pb !== null,
    tier: 'standard',
    partnerClientId: null,
    name: brandName,
    tagline: '',
    description: '',
    domain: brandDomain,
    adminControlDomain: null,
    logo: {
      full: pb ? pb.logoUrl : '',
      icon: logoIcon,
      footer: pb ? pb.logoUrl : '',
      mobileNav: pb ? pb.logoUrl : '',
      favicon: '',
    },
    colors: {
      primary: accent,
      primaryDark: accentDark,
      primaryLight: accent,
      dark: '#111827',
      gray: '#6b7280',
      grayLight: '#f3f4f6',
      white: '#ffffff',
      offWhite: '#f9fafb',
      border: '#e5e7eb',
      accent,
      accentDark,
    },
    fonts: { body: 'Poppins', display: 'Poppins' },
    nav: [],
    cta: { label: 'Book a Demo', href: pb ? '' : 'https://workforcewave.com/demo' },
    contact: { email: '', phone: '', demoPhone: '' },
    seo: {
      defaultDescription: '',
      ogImage: '',
      twitterHandle: '',
      siteUrl: brandDomain ? `https://${brandDomain}` : '',
      titleSuffix: '',
      authors: [],
    },
    footer: { columns: [], copyright: '', industries: [], integrations: [] },
    features: {} as ResolvedBrand['features'],
    copyOverrides: {},
    marketingConfig: null,
    collections: { prefix: '' },
    catalyst: {
      agentId,
      agentName: config.agentName ?? 'Catalyst',
      // Clean visitor-facing pill label from the agent's web_widget_button_text.
      // Lets the collapsed bubble read e.g. "Chat with us" instead of exposing the
      // internal agent name (which often carries admin suffixes like "(PRIMARY)").
      // Trimmed defensively; empty/whitespace → undefined → default greeting.
      buttonText: config.buttonText?.trim() || undefined,
    },
    googleAnalyticsId: null,
  }
}

// ---------------------------------------------------------------------------
// Root embed app (handles consent gate + widget render)
// ---------------------------------------------------------------------------

/**
 * EmbedApp is the React root component rendered inside the shadow DOM.
 * It manages the consent gate (EU visitors only) and conditionally renders
 * the Catalyst widget after consent is granted (or immediately for non-EU).
 *
 * Props flow: agentId + config -> ResolvedBrand -> CatalystInnerEmbed
 */
function EmbedApp({
  agentId,
  config,
  languages,
  modes,
  hideLauncher,
}: {
  agentId: string
  config: AgentConfig
  languages: string[]
  modes: ('voice' | 'text')[]
  hideLauncher: boolean
}) {
  // Detect EU timezone once on mount -- result is stable for the page lifetime.
  const isEu = isEuTimezone()

  // Consent state:
  //   null     = not yet determined (checking localStorage on mount)
  //   'needed' = EU visitor with no stored consent -- show banner
  //   'granted'= consent given (via storage or user click)
  //   'declined'= user declined in this session
  const [consentState, setConsentState] = useState<'checking' | 'needed' | 'granted' | 'declined'>('checking')

  /**
   * On mount, check whether we already have stored consent.
   * For non-EU visitors we skip the consent check and go straight to 'granted'.
   * For EU visitors we read localStorage; if 'granted' skip banner, else show it.
   */
  useEffect(() => {
    if (!isEu) {
      // Non-EU: no consent needed. Single setState batched into the initial
      // commit -- the cascading-render concern doesn't apply.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setConsentState('granted')
      return
    }

    const stored = readConsentFromStorage()
    if (stored === 'granted') {
      setConsentState('granted')
    } else {
      // No consent or declined -- show the banner
      setConsentState('needed')
    }
  }, [isEu])

  /** Brand config synthesised from the API response for Catalyst's brand prop. */
  const brand = buildEmbedBrand(agentId, config)

  const accentColor = config.color ?? '#4f46e5'

  // Show nothing while determining consent (avoid flash)
  if (consentState === 'checking') return null

  // User declined -- render nothing (no widget)
  if (consentState === 'declined') return null

  // EU visitor needs consent -- show the banner only
  if (consentState === 'needed') {
    return (
      <ConsentBanner
        accentColor={accentColor}
        onAccept={() => {
          writeConsentToStorage('granted')
          setConsentState('granted')
        }}
        onDecline={() => setConsentState('declined')}
      />
    )
  }

  // Consent granted (or non-EU): render the full Catalyst widget
  return (
    <ConversationProvider>
      <CatalystInnerEmbed
        brand={brand}
        languages={languages}
        modes={modes}
        hideLauncher={hideLauncher}
      />
    </ConversationProvider>
  )
}

// ---------------------------------------------------------------------------
// Mount logic
// ---------------------------------------------------------------------------

/**
 * Resolve the API origin the widget should call for config, signed-url, chat,
 * and crm requests.
 *
 * The embed bundle is served BY the WFW backend (workforcewave.com, or a
 * white-label partner host that proxies to it), so the origin of the embed's
 * own <script src> is always the correct backend -- even when the widget is
 * pasted on a fully independent third-party site (e.g. codeandtrust.com) whose
 * own origin has no /api/voice-widget or /api/catalyst routes.
 *
 * We deliberately do NOT use window.location.origin: that only works when the
 * page host happens to be the WFW backend (WFW's own site or a WFW-proxied
 * partner domain). It silently breaks on independent embeds, which is the whole
 * point of a paste-anywhere widget.
 *
 * Falls back to window.location.origin when the script src can't be parsed,
 * preserving the prior behavior for same-origin / WFW-proxied installs.
 */
function resolveApiBase(scriptTag: HTMLScriptElement): string {
  try {
    if (scriptTag.src) return new URL(scriptTag.src).origin
  } catch {
    // Unparseable src (shouldn't happen for a loaded script) -- fall through.
  }
  return window.location.origin
}

/**
 * Main mount function.
 * Called once on DOMContentLoaded (or immediately if the DOM is already ready).
 * All errors are caught and logged with the [wfw-embed] prefix.
 */
async function mount(scriptTag: HTMLScriptElement | null): Promise<void> {
  if (!scriptTag) {
    console.log('[wfw-embed] Could not find script tag with data-agent-id. Widget not mounted.')
    return
  }

  // Trim the agent id: a stray newline/space in data-agent-id (easy to introduce
  // when a customer pastes the embed snippet, or from a build-time env var with a
  // trailing newline) would make the exact-match config lookup miss and silently
  // not mount. The old raw <elevenlabs-convai> element tolerated whitespace; we do too.
  const agentId = (scriptTag.dataset.agentId ?? '').trim()

  if (!agentId || !scriptTag.isConnected) {
    console.log('[wfw-embed] data-agent-id is empty. Widget not mounted.')
    return
  }

  // Optional: data-hide-launcher="true" suppresses the collapsed floating launcher.
  // The widget still mounts and still responds to catalyst:open / catalyst:open-voice,
  // so a host page can drive it entirely from its own CTA (e.g. an inline button)
  // without a second floating launcher appearing. Defaults to false (launcher shown).
  const hideLauncher = (scriptTag.dataset.hideLauncher ?? '').trim() === 'true'

  // Resolve the backend origin from the embed script itself (see resolveApiBase)
  // so config + all runtime calls hit the WFW backend that served the bundle,
  // not the host page's origin.
  const apiBase = resolveApiBase(scriptTag)

  // Point every Catalyst backend call (config, signed-url, chat, crm,
  // voice/debug) at the same resolved origin. Catalyst defaults to relative
  // URLs (same-origin) for the in-app dashboard and marketing site; the embed
  // overrides that default here, once, before the widget renders.
  setCatalystApiBase(apiBase)

  const lifetime = createAgentLifetime(scriptTag)
  try {
    // -- 2. Fetch agent config -------------------------------------------
    let config: AgentConfig

    try {
      const resp = await fetch(
        `${apiBase}/api/voice-widget/config/${encodeURIComponent(agentId)}`,
        { credentials: 'omit', signal: lifetime.signal }
      )

      if (!lifetime.active) return

      if (!resp.ok) {
        console.log(`[wfw-embed] Config fetch failed: HTTP ${resp.status}. Widget not mounted.`)
        return
      }

      config = (await resp.json()) as AgentConfig
    } catch (err) {
      if (!lifetime.active) return
      const msg = err instanceof Error ? err.message : String(err)
      console.log(`[wfw-embed] Config fetch error: ${msg}. Widget not mounted.`)
      return
    }

    if (!lifetime.active) return

    // -- 3. Kill-switch: widget disabled in admin -------------------------
    if (!config.enabled) {
      // Silent exit -- the admin explicitly disabled this widget
      console.log(`[wfw-embed] Agent ${agentId} web widget is disabled. Widget not mounted.`)
      return
    }

    // -- 4. Client-side origin validation (defense-in-depth) --------------
    // The server also validates origin on every signed-URL request.
    // We validate here too so we don't inject DOM at all on disallowed origins.
    const currentOrigin = window.location.origin
    const allowedOrigins = config.allowedOrigins ?? []

    if (allowedOrigins.length > 0 && !isOriginAllowed(currentOrigin, allowedOrigins)) {
      console.log(
        `[wfw-embed] Origin "${currentOrigin}" is not allowed for agent ${agentId}. Widget not mounted.`
      )
      return
    }

    // -- 5. Validate and normalise languages + modes ----------------------
    // Accepted language codes must be valid ISO 639-1 codes from our supported set.
    // Unrecognised codes are silently dropped; at least 'en' is always included.
    const rawLanguages = Array.isArray(config.languages) ? config.languages : ['en']
    const languages = rawLanguages.filter(isValidLanguageCode)
    const finalLanguages = languages.length > 0 ? languages : ['en']

    // Modes: only 'voice' and 'text' are accepted values.
    const rawModes = Array.isArray(config.modes) ? config.modes : ['voice', 'text']
    const validModes = rawModes.filter(
      (m): m is 'voice' | 'text' => m === 'voice' || m === 'text'
    )
    const finalModes = validModes.length > 0 ? validModes : (['voice', 'text'] as ('voice' | 'text')[])

    // -- 6. Create shadow DOM host ----------------------------------------
    // The host div is appended to body. Fixed positioning is set on the host
    // (not inside shadow DOM) so the widget sits above all page content.
    //
    // Multi-widget support: a single page can carry more than one WFW embed —
    // e.g. a site running BOTH a text support bot and a separate voice demo agent
    // (each pasted as its own agent.js <script data-agent-id>). Each script mounts
    // an independent widget, but they all default to the bottom-right corner and
    // would overlap. We do two things to make multiple widgets coexist cleanly:
    //
    //   (a) Dedupe by agent id: if THIS agent already has a host on the page (e.g.
    //       the same embed was injected twice, or a framework double-fired the
    //       loader), skip — never stack a widget on top of itself.
    //   (b) Stack distinct agents: offset each subsequent widget upward by a fixed
    //       gap so every collapsed launcher remains visible instead of hiding the
    //       one beneath it.
    //
    // Backward-compatible: on the overwhelmingly common single-widget page,
    // existingHosts is empty → stackOffset is 0 → identical to the prior behavior.
    const WFW_EMBED_HOST_ATTR = 'data-wfw-embed-root'
    // Vertical gap between stacked launchers, in px. Sized to clear a collapsed
    // launcher pill (button + "Powered by" attribution) so two never overlap.
    const WFW_WIDGET_STACK_GAP_PX = 92

    const existingHosts = Array.from(
      document.querySelectorAll<HTMLElement>(`[${WFW_EMBED_HOST_ATTR}]`)
    )

    // (a) Dedupe: this exact agent is already mounted on the page — bail out.
    if (existingHosts.some((el) => el.dataset.wfwAgentId === agentId)) {
      console.log(
        `[wfw-embed] Agent ${agentId} is already mounted on this page. Skipping duplicate mount.`
      )
      return
    }

    const host = document.createElement('div')
    // Keep the legacy id for back-compat (existing selectors / docs reference it),
    // and add a stable attribute + per-agent marker for multi-widget bookkeeping.
    // (ids are not required to be unique for getElementById, but duplicate ids are
    // invalid HTML; the attribute is the canonical hook for multi-widget logic.)
    host.id = 'wfw-embed-root'
    host.setAttribute(WFW_EMBED_HOST_ATTR, '')
    host.dataset.wfwAgentId = agentId

    // (b) Stack: lift each subsequent widget above the previously-mounted ones.
    const stackOffset = existingHosts.length * WFW_WIDGET_STACK_GAP_PX
    host.style.cssText =
      `position: fixed; bottom: ${stackOffset}px; right: 0; z-index: 2147483647; pointer-events: none;`
    document.body.appendChild(host)

    const shadow = host.attachShadow({ mode: 'open' })

    // -- 7. Inject CSS into shadow root -----------------------------------
    // Fonts and all widget CSS are injected here so they are scoped to the widget.
    const styleEl = document.createElement('style')
    try {
      styleEl.textContent = typeof __WFW_AGENT_CSS__ !== 'undefined' ? __WFW_AGENT_CSS__ : ''
    } catch {
      styleEl.textContent = ''
    }
    shadow.appendChild(styleEl)

    // Poppins font link -- injected separately because @import inside <style>
    // inside shadow DOM has inconsistent support in older Chrome versions.
    const fontLink = document.createElement('link')
    fontLink.rel = 'stylesheet'
    fontLink.href = 'https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap'
    shadow.appendChild(fontLink)

    // Container for React -- pointer-events re-enabled on this inner div
    const container = document.createElement('div')
    container.style.cssText = 'pointer-events: auto;'
    shadow.appendChild(container)

    // -- 8. Render the React widget --------------------------------------
    const root = createRoot(container)
    if (!lifetime.attach(host, root)) return

    root.render(
      <React.StrictMode>
        <EmbedApp
          agentId={agentId}
          config={config}
          languages={finalLanguages}
          modes={finalModes}
          hideLauncher={hideLauncher}
        />
      </React.StrictMode>
    )
  } finally {
    if (!lifetime.mounted) lifetime.dispose()
  }
}

// ---------------------------------------------------------------------------
// Execution
// ---------------------------------------------------------------------------

// Capture the executing script before DOM readiness or config fetch yields.
// Otherwise an older execution can accidentally adopt a newly injected agent.
const scriptTags = document.querySelectorAll<HTMLScriptElement>('script[data-agent-id]')
const scriptTag = document.currentScript as HTMLScriptElement | null
  ?? scriptTags[scriptTags.length - 1]
  ?? null
const start = () => void mount(scriptTag)

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', start, { once: true })
} else {
  start()
}
